function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') : ''
}

async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify(body || {}),
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(data.error || 'Request failed')
  }
  return data
}

async function deleteRequest(url) {
  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Request failed')
  }
}

async function getJson(url) {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(data.error || 'Request failed')
  }
  return data
}

function supportsWebAuthn() {
  return (
    typeof window.PublicKeyCredential === 'function' &&
    typeof navigator.credentials?.create === 'function' &&
    typeof navigator.credentials?.get === 'function'
  )
}

async function supportsConditionalMediation() {
  if (
    !supportsWebAuthn() ||
    typeof window.PublicKeyCredential.isConditionalMediationAvailable !==
      'function'
  ) {
    return false
  }

  try {
    return await window.PublicKeyCredential.isConditionalMediationAvailable()
  } catch (_error) {
    return false
  }
}

function parseCreationOptions(options) {
  if (
    typeof window.PublicKeyCredential.parseCreationOptionsFromJSON ===
    'function'
  ) {
    return window.PublicKeyCredential.parseCreationOptionsFromJSON(options)
  }
  throw new Error(
    'This browser does not support passkey registration. Try a current Chrome, Safari, or Firefox.'
  )
}

function parseRequestOptions(options) {
  if (
    typeof window.PublicKeyCredential.parseRequestOptionsFromJSON === 'function'
  ) {
    return window.PublicKeyCredential.parseRequestOptionsFromJSON(options)
  }
  throw new Error(
    'This browser does not support passkey sign-in. Try a current Chrome, Safari, or Firefox.'
  )
}

function credentialToJSON(credential) {
  if (typeof credential.toJSON === 'function') {
    return credential.toJSON()
  }
  throw new Error('This browser does not support passkey JSON serialization')
}

function showError(element, message) {
  if (!element) return
  element.hidden = !message
  element.textContent = message || ''
}

function formatDate(value) {
  if (!value) return null
  const date = new Date(value)
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function buildPasskeyItem(credential, urlBase) {
  const wrapper = document.createElement('div')
  wrapper.className = 'box passkey-item'
  wrapper.dataset.passkeyId = credential.id
  wrapper.dataset.destroyUrl = `${urlBase}/${credential.id}`

  const lastUsed = formatDate(credential.last_used_at)
  const created = formatDate(credential.created_at)

  wrapper.innerHTML = `
    <div class="level is-mobile mb-0">
      <div class="level-left">
        <div class="level-item">
          <div>
            <p class="has-text-weight-semibold passkey-nickname"></p>
            <p class="is-size-7 has-text-grey">
              Added ${created || 'just now'}${
    lastUsed ? ` · Last used ${lastUsed}` : ''
  }
            </p>
          </div>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <button type="button"
                  class="button is-small is-danger is-light passkey-remove"
                  title="Remove passkey"
                  aria-label="Remove passkey">
            <span class="icon is-small"><i class="fas fa-minus" aria-hidden="true"></i></span>
          </button>
        </div>
      </div>
    </div>
  `

  wrapper.querySelector('.passkey-nickname').textContent = credential.nickname
  return wrapper
}

function passkeyErrorMessage(error) {
  if (!error) return 'Could not add passkey'
  if (error.name === 'NotAllowedError') {
    return 'Passkey creation was cancelled or blocked by the browser.'
  }
  if (error.name === 'InvalidStateError') {
    return 'That passkey is already registered on this account.'
  }
  return error.message || 'Could not add passkey'
}

async function addPasskey(addButton) {
  const root = document.getElementById('passkeys-manager')
  const nicknameInput = document.getElementById('passkey-nickname')
  const errorEl = document.getElementById('passkeys-error')
  const list = document.getElementById('passkeys-list')

  if (!root || root.dataset.impersonating === 'true') return

  const nickname = nicknameInput?.value?.trim() || ''
  showError(errorEl, '')

  if (!nickname) {
    showError(errorEl, 'Please enter a name for this passkey.')
    nicknameInput?.focus()
    return
  }

  addButton.disabled = true

  try {
    if (!supportsWebAuthn()) {
      throw new Error('Passkeys are not supported in this browser.')
    }

    const options = await getJson(root.dataset.creationOptionsUrl)
    const credential = await navigator.credentials.create({
      publicKey: parseCreationOptions(options),
    })

    if (!credential) {
      throw new Error('Passkey creation was cancelled')
    }

    const created = await postJson(root.dataset.createUrl, {
      publicKeyCredential: credentialToJSON(credential),
      nickname: nickname,
    })

    document.getElementById('passkeys-empty')?.remove()
    const item = buildPasskeyItem(created, root.dataset.createUrl)
    list.appendChild(item)
    if (nicknameInput) nicknameInput.value = ''
  } catch (error) {
    console.error('Passkey registration failed', error)
    showError(errorEl, passkeyErrorMessage(error))
  } finally {
    addButton.disabled = false
  }
}

async function removePasskey(item) {
  if (!window.confirm('Remove this passkey?')) return

  try {
    await deleteRequest(item.dataset.destroyUrl)
    item.remove()
    const list = document.getElementById('passkeys-list')
    if (list && !list.querySelector('.passkey-item')) {
      const empty = document.createElement('p')
      empty.className = 'has-text-grey mb-3'
      empty.id = 'passkeys-empty'
      empty.textContent = 'No passkeys yet.'
      list.appendChild(empty)
    }
  } catch (error) {
    window.alert(error.message)
  }
}

function bindPasskeyManagerClicks() {
  document.addEventListener('click', (event) => {
    if (document.body.dataset.passkeysEnabled !== 'true') return

    const addButton = event.target.closest('#passkey-add')
    if (addButton) {
      event.preventDefault()
      addPasskey(addButton)
      return
    }

    const removeButton = event.target.closest('.passkey-remove')
    if (removeButton) {
      event.preventDefault()
      const item = removeButton.closest('.passkey-item')
      if (item) removePasskey(item)
    }
  })
}

async function initPasskeyLogin() {
  const root = document.getElementById('passkey-login')
  if (!root) return

  const errorEl = document.getElementById('passkey-login-error')
  if (!(await supportsConditionalMediation())) return

  let abortController = new AbortController()

  const startConditional = async () => {
    try {
      const options = await getJson(root.dataset.optionsUrl)
      const credential = await navigator.credentials.get({
        publicKey: parseRequestOptions(options),
        mediation: 'conditional',
        signal: abortController.signal,
      })

      if (!credential) return

      const result = await postJson(root.dataset.createUrl, {
        publicKeyCredential: credentialToJSON(credential),
      })

      window.location.href = result.redirect_url || '/'
    } catch (error) {
      if (error.name === 'AbortError') return
      if (error.name === 'NotAllowedError') {
        abortController = new AbortController()
        startConditional()
        return
      }
      console.error('Passkey sign-in failed', error)
      showError(errorEl, error.message || 'Passkey sign-in failed')
      abortController = new AbortController()
      startConditional()
    }
  }

  startConditional()
}

function markUnsupportedAddButton() {
  const root = document.getElementById('passkeys-manager')
  const addButton = document.getElementById('passkey-add')
  const errorEl = document.getElementById('passkeys-error')
  if (!root || !addButton) return

  if (!supportsWebAuthn()) {
    addButton.disabled = true
    showError(errorEl, 'Passkeys are not supported in this browser.')
  }
}

let passkeysInitialized = false

export function initPasskeys() {
  if (document.body.dataset.passkeysEnabled !== 'true') return
  if (passkeysInitialized) {
    markUnsupportedAddButton()
    return
  }

  passkeysInitialized = true
  bindPasskeyManagerClicks()
  markUnsupportedAddButton()
  initPasskeyLogin()
}
